/**
 * Mobile Bottom Navigation
 *
 * App-style bottom bar for authenticated users on small screens (≤1024px).
 * Shows the same 3 main nav items as the desktop sidebar: Book Appointments,
 * Manage Appointments, My Account. Desktop sidebar is unchanged.
 */

import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { cn } from "@/shared/utils/commonUtils";
import { Features } from "@/features";
import type { FeatureModule } from "@/shared/types/routing";

interface NavItem {
  path: string;
  label: string;
  icon?: React.ComponentType<{ size?: number | string; className?: string }>;
}

function getBottomNavItems(untitledLabel: string): NavItem[] {
  const routes = _.flatMap(Features as FeatureModule[], (f) => f.routes || []);
  return routes
    .filter((r) => r.meta?.showInSidebar)
    .sort((a, b) => (a.meta?.order ?? 99) - (b.meta?.order ?? 99))
    .map((r) => ({
      path: r.path.startsWith("/") ? r.path : `/${r.path}`,
      label: r.meta?.label ?? untitledLabel,
      icon: r.meta?.icon,
    }));
}

export function MobileBottomNav() {
  const { t } = useTranslation("common");
  const location = useLocation();

  const items = useMemo(() => getBottomNavItems(t("layout.sidebar.untitled")), [t]);

  const getShortLabel = (path: string) => {
    if (path.startsWith("/book-appointment")) return t("layout.sidebar.bookShort");
    if (path.startsWith("/manage-appointments")) return t("layout.sidebar.manageShort");
    if (path.startsWith("/my-account")) return t("layout.sidebar.accountShort");
    const item = items.find((i) => i.path === path);
    return item ? t(item.label) : t("layout.sidebar.untitled");
  };

  const isActive = (path: string) => {
    if (location.pathname === path) return true;
    if (path !== "/" && location.pathname.startsWith(path + "/")) return true;
    return false;
  };

  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-sidebar border-t border-sidebar-border"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="flex items-center justify-around h-16 px-2 max-w-[100vw]">
        {items.map((item) => {
          const active = isActive(item.path);
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 py-2 rounded-lg transition-colors duration-200",
                "touch-manipulation active:scale-[0.98]",
                active
                  ? "text-white bg-white/10"
                  : "text-sidebar-foreground/80 hover:bg-white/5 hover:text-white"
              )}
              aria-current={active ? "page" : undefined}
            >
              {Icon && (
                <Icon
                  className={cn("size-6 shrink-0", active ? "opacity-100" : "opacity-80")}
                  aria-hidden
                />
              )}
              <span className="text-[9px] font-medium leading-tight truncate w-full max-w-full px-0.5 text-center overflow-hidden">
                {getShortLabel(item.path)}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
